/**
 * useHeatmap – Market Heatmap store (Zustand).
 *
 * 1D only: the backend price cache carries daily change only, so the old
 * 1W/1M/YTD period plumbing silently rendered 1D data whatever was picked.
 * The period selector and param were removed rather than fake period returns.
 */
import { create } from 'zustand'
import { getMarketHeatmap } from '@/lib/api'
import type { HeatmapData } from '@/lib/types'

/** Extract a human-readable message from an Axios-ish error. Exported for tests. */
export function heatmapErrorMessage(e: unknown): string {
  const err = e as { response?: { data?: { detail?: unknown } }; message?: unknown } | null
  const detail = err?.response?.data?.detail
  if (typeof detail === 'string' && detail) return detail
  if (typeof err?.message === 'string' && err.message) return err.message
  return String(e)
}

interface HeatmapState {
  data: HeatmapData | null
  loading: boolean
  error: string | null
  index: string

  setIndex: (index: string) => void
  fetchHeatmap: () => Promise<void>
}

export const useHeatmap = create<HeatmapState>()((set, get) => ({
  data: null,
  loading: false,
  error: null,
  index: 'all',

  setIndex: (index) => {
    set({ index })
    get().fetchHeatmap()
  },

  fetchHeatmap: async () => {
    const { index } = get()
    set({ loading: true, error: null })
    try {
      const data = await getMarketHeatmap(index)
      set({ data, loading: false })
    } catch (e) {
      // Show the API's detail message, not "AxiosError: Request failed…"
      set({ error: heatmapErrorMessage(e), loading: false })
    }
  },
}))
